import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { StatusTimelineNode } from '@/utils/residents/maintenance/statusTimeline';

interface StatusTimelineProps {
  nodes: StatusTimelineNode[];
}

export const StatusTimeline: React.FC<StatusTimelineProps> = ({ nodes }) => {
  const { colors } = useThemeColors();

  return (
    <View style={styles.container}>
      {nodes.map((node, index) => {
        const isLast = index === nodes.length - 1;
        const dotColor = node.state === 'upcoming' ? colors.glass.buttonBorder : node.color;
        const lineColor = node.state === 'done' ? node.color : colors.glass.buttonBg;
        return (
          <View key={node.key} style={styles.row}>
            <View style={styles.rail}>
              <View
                style={[
                  styles.dot,
                  { backgroundColor: dotColor },
                  node.state === 'active' && [styles.dotActive, { borderColor: node.color }],
                ]}
              />
              {!isLast && <View style={[styles.line, { backgroundColor: lineColor }]} />}
            </View>
            <View style={[styles.body, isLast && styles.bodyLast]}>
              <Text
                style={[
                  styles.label,
                  { color: node.state === 'upcoming' ? colors.text.dim : colors.text.primary },
                ]}
              >
                {node.label}
              </Text>
              {node.date ? (
                <Text style={[styles.date, { color: colors.text.muted }]}>{node.date}</Text>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
};

const DOT = 14;

const styles = StyleSheet.create({
  container: { gap: 0 },
  row: { flexDirection: 'row', gap: 12 },
  rail: { width: DOT, alignItems: 'center' },
  dot: { width: DOT, height: DOT, borderRadius: DOT / 2 },
  dotActive: { borderWidth: 3, backgroundColor: 'transparent' },
  line: { width: 2, flex: 1, minHeight: 24, marginVertical: 2 },
  body: { flex: 1, paddingBottom: 20 },
  // The last milestone needs no rail spacing below it — without this the
  // card's bottom whitespace reads double its top.
  bodyLast: { paddingBottom: 0 },
  label: { ...REDESIGN_TYPOGRAPHY.cardTitle, lineHeight: 20 },
  date: { ...REDESIGN_TYPOGRAPHY.caption, lineHeight: 16, marginTop: 2 },
});
